import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface TableParamsState {
  page: number;
  limit: number;
  searchTerm: string;
  filters: Record<string, string>;
}

interface TableStoreState {
  tables: Record<string, TableParamsState>;
  currentModule: string | null;
  trackModuleNavigation: (newModule: string) => void;
  getTableParams: (tableKey: string, defaultLimit?: number) => TableParamsState;
  setPage: (tableKey: string, page: number) => void;
  setLimit: (tableKey: string, limit: number, defaultLimit?: number) => void;
  setSearchTerm: (tableKey: string, searchTerm: string) => void;
  setFilter: (tableKey: string, filterKey: string, value: string) => void;
  setAllParams: (tableKey: string, params: Partial<TableParamsState>) => void;
  resetTable: (tableKey: string, defaultLimit?: number) => void;
}

const DEFAULT_LIMIT = 10;
const DEFAULT_FILTERS: Record<string, string> = {};
export const DEFAULT_TABLE_STATE: TableParamsState = Object.freeze({
  page: 1,
  limit: DEFAULT_LIMIT,
  searchTerm: "",
  filters: DEFAULT_FILTERS,
});

export const useTableStore = create<TableStoreState>()(
  persist(
    (set, get) => ({
      tables: {},
      currentModule: null,

      trackModuleNavigation: (newModule: string) => {
        const { currentModule, tables } = get();
        if (currentModule && currentModule !== newModule) {
          set({
            currentModule: newModule,
            tables: {
              ...tables,
              [currentModule]: DEFAULT_TABLE_STATE,
            },
          });
        } else if (currentModule !== newModule) {
          set({ currentModule: newModule });
        }
      },

      getTableParams: (tableKey: string, defaultLimit = DEFAULT_LIMIT) => {
        const existing = get().tables[tableKey];
        if (existing) return existing;
        return { ...DEFAULT_TABLE_STATE, limit: defaultLimit };
      },

      setPage: (tableKey: string, page: number) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableKey]: {
              ...(state.tables[tableKey] || DEFAULT_TABLE_STATE),
              page,
            },
          },
        })),

      setLimit: (tableKey: string, limit: number, defaultLimit = DEFAULT_LIMIT) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableKey]: {
              ...(state.tables[tableKey] || DEFAULT_TABLE_STATE),
              limit,
              page: 1,
            },
          },
        })),

      setSearchTerm: (tableKey: string, searchTerm: string) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableKey]: {
              ...(state.tables[tableKey] || DEFAULT_TABLE_STATE),
              searchTerm,
              page: 1,
            },
          },
        })),

      setFilter: (tableKey: string, filterKey: string, value: string) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableKey]: {
              ...(state.tables[tableKey] || DEFAULT_TABLE_STATE),
              filters: {
                ...(state.tables[tableKey]?.filters || DEFAULT_FILTERS),
                [filterKey]: value,
              },
              page: 1,
            },
          },
        })),

      setAllParams: (tableKey: string, params: Partial<TableParamsState>) =>
        set((state) => {
          const current = state.tables[tableKey] || DEFAULT_TABLE_STATE;
          return {
            tables: {
              ...state.tables,
              [tableKey]: {
                ...current,
                ...params,
                filters: {
                  ...current.filters,
                  ...params.filters,
                },
              },
            },
          };
        }),

      resetTable: (tableKey: string, defaultLimit = DEFAULT_LIMIT) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableKey]: DEFAULT_TABLE_STATE,
          },
        })),
    }),
    {
      name: "table-params-storage",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
